import { z } from 'zod';

import { entityIdSchema, isoDateSchema, isoDateTimeSchema, pageMetaSchema } from './common.js';
import {
  canonicalizeSignedStockQuantity,
  canonicalizeStockQuantity,
  stockQuantityToMilliunits,
} from './stock-quantity.js';

export const stockCanonicalIsoInstantSchema = isoDateTimeSchema.refine((value) => {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/u.test(value)) return false;
  try {
    return new Date(value).toISOString() === value;
  } catch {
    return false;
  }
}, 'Instant UTC canonique attendu.');

export const stockQuantitySchema = z
  .string()
  .trim()
  .refine((value) => canonicalizeStockQuantity(value) !== null, {
    message: 'La quantité doit être une décimale positive avec au maximum trois décimales.',
  })
  .transform((value) => canonicalizeStockQuantity(value) ?? value);

export const positiveStockQuantitySchema = stockQuantitySchema.refine(
  (value) => stockQuantityToMilliunits(value) > 0,
  'La quantité doit être strictement positive.',
);

export const signedStockQuantitySchema = z
  .string()
  .trim()
  .refine((value) => canonicalizeSignedStockQuantity(value) !== null, {
    message: 'La quantité signée accepte au maximum trois décimales.',
  })
  .transform((value) => canonicalizeSignedStockQuantity(value) ?? value);

export const stockScopeSchema = z.enum(['group', 'camp']);
export const stockItemStateSchema = z.enum(['active', 'archived']);

export const STOCK_LOCATION_LABEL_MAX_UTF16 = 180;
export const STOCK_LOCATION_DESCRIPTION_MAX_UTF16 = 1_000;
export const STOCK_ITEM_NAME_MAX_UTF16 = 200;
export const STOCK_ITEM_REFERENCE_MAX_UTF16 = 120;
export const STOCK_ITEM_UNIT_MAX_UTF16 = 40;
export const STOCK_MOVEMENT_NOTES_MAX_UTF16 = 2_000;
export const STOCK_CANONICAL_TEXT_MAX_EXPANSION_UTF16 = 18;
export const STOCK_CANONICAL_NAME_MAX_UTF16 =
  STOCK_ITEM_NAME_MAX_UTF16 * STOCK_CANONICAL_TEXT_MAX_EXPANSION_UTF16;

export const STOCK_CANONICAL_TEXT_CURSOR_BOUNDARY_AUTHORITY = {
  version: 1,
  lengthSemantics: 'ECMASCRIPT_UTF16_CODE_UNITS',
  sourceNameMaximumUtf16: STOCK_ITEM_NAME_MAX_UTF16,
  canonicalizationMaximumExpansionUtf16: STOCK_CANONICAL_TEXT_MAX_EXPANSION_UTF16,
  persistedAndCursorKeyMaximumUtf16: STOCK_CANONICAL_NAME_MAX_UTF16,
  cursorTransport: 'VERSIONED_UTF16BE_BASE64URL',
  truncation: false,
} as const;

export const STOCK_LEGACY_RUNTIME_REPRESENTABILITY_AUTHORITY = {
  version: 1,
  lengthSemantics: 'ECMASCRIPT_ZOD_UTF16_CODE_UNITS',
  runtimeSchemas: ['stockLocationSchema', 'stockItemSchema', 'stockMovementSchema'],
  canonicalTextOrder: ['NFKC', 'UNICODE_WHITESPACE', 'TRIM', 'CASE_FOLD'],
  refusalPrefix: 'MIGRATION_STOCK_',
} as const;

export const stockLocationLabelSchema = z
  .string()
  .trim()
  .min(1)
  .max(STOCK_LOCATION_LABEL_MAX_UTF16);
export const stockLocationDescriptionSchema = z
  .string()
  .trim()
  .max(STOCK_LOCATION_DESCRIPTION_MAX_UTF16);
export const stockItemNameSchema = z.string().trim().min(1).max(STOCK_ITEM_NAME_MAX_UTF16);
export const stockItemReferenceSchema = z.string().trim().max(STOCK_ITEM_REFERENCE_MAX_UTF16);
export const stockItemUnitSchema = z.string().trim().min(1).max(STOCK_ITEM_UNIT_MAX_UTF16);
export const stockMovementNotesInputSchema = z.string().trim().max(STOCK_MOVEMENT_NOTES_MAX_UTF16);
export const stockMovementNotesSchema = z.string().max(STOCK_MOVEMENT_NOTES_MAX_UTF16);
export const stockCanonicalNameKeySchema = z.string().min(1).max(STOCK_CANONICAL_NAME_MAX_UTF16);

export const stockLocationInputSchema = z
  .object({
    label: stockLocationLabelSchema,
    description: stockLocationDescriptionSchema.default(''),
  })
  .strict();

export const stockLocationSchema = stockLocationInputSchema.extend({
  id: entityIdSchema,
  active: z.boolean(),
  createdAt: stockCanonicalIsoInstantSchema,
  updatedAt: stockCanonicalIsoInstantSchema,
});

export const stockItemCreateInputSchema = z
  .object({
    locationId: entityIdSchema.nullable().default(null),
    scope: stockScopeSchema,
    name: stockItemNameSchema,
    reference: stockItemReferenceSchema.default(''),
    unit: stockItemUnitSchema,
    openingQuantity: stockQuantitySchema.default('0'),
    alertThreshold: stockQuantitySchema.default('0'),
  })
  .strict();

export const stockItemUpdateInputSchema = z
  .object({
    locationId: entityIdSchema.nullable().optional(),
    name: stockItemNameSchema.optional(),
    reference: stockItemReferenceSchema.optional(),
    unit: stockItemUnitSchema.optional(),
    alertThreshold: stockQuantitySchema.optional(),
    revision: z.number().int().positive(),
  })
  .strict();

export const stockItemArchiveInputSchema = z
  .object({ revision: z.number().int().positive() })
  .strict();

export const stockItemSchema = z
  .object({
    id: entityIdSchema,
    scope: stockScopeSchema,
    name: stockItemNameSchema,
    reference: stockItemReferenceSchema,
    locationId: entityIdSchema.nullable(),
    unit: stockItemUnitSchema,
    quantityTotal: stockQuantitySchema,
    quantityAvailable: stockQuantitySchema,
    alertThreshold: stockQuantitySchema,
    active: z.boolean(),
    state: stockItemStateSchema,
    revision: z.number().int().positive(),
    createdAt: stockCanonicalIsoInstantSchema,
    updatedAt: stockCanonicalIsoInstantSchema,
  })
  .strict();

const stockItemCursorPayloadSchema = z
  .object({ normalizedName: stockCanonicalNameKeySchema, id: entityIdSchema })
  .strict();

export type StockItemCursor = z.infer<typeof stockItemCursorPayloadSchema>;

const STOCK_ITEM_CURSOR_COMPACT_PREFIX = 's1.';
const STOCK_ITEM_CURSOR_BASE64URL_ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

function appendStockCursorUtf16Bytes(bytes: number[], value: string): void {
  for (let index = 0; index < value.length; index += 1) {
    const codeUnit = value.charCodeAt(index);
    bytes.push(codeUnit >>> 8, codeUnit & 0xff);
  }
}

function encodeStockCursorBytes(bytes: readonly number[]): string {
  let encoded = '';
  for (let index = 0; index < bytes.length; index += 3) {
    const first = bytes[index] ?? 0;
    const second = bytes[index + 1];
    const third = bytes[index + 2];
    encoded += STOCK_ITEM_CURSOR_BASE64URL_ALPHABET.charAt(first >>> 2);
    encoded += STOCK_ITEM_CURSOR_BASE64URL_ALPHABET.charAt(
      ((first & 0x03) << 4) | ((second ?? 0) >>> 4),
    );
    if (second !== undefined) {
      encoded += STOCK_ITEM_CURSOR_BASE64URL_ALPHABET.charAt(
        ((second & 0x0f) << 2) | ((third ?? 0) >>> 6),
      );
    }
    if (third !== undefined) {
      encoded += STOCK_ITEM_CURSOR_BASE64URL_ALPHABET.charAt(third & 0x3f);
    }
  }
  return encoded;
}

function decodeStockCursorBytes(encoded: string): number[] | null {
  if (!/^[A-Za-z0-9_-]+$/u.test(encoded) || encoded.length % 4 === 1) return null;
  const bytes: number[] = [];
  let remainingBits = 0;
  let remainingBitCount = 0;
  for (const character of encoded) {
    const value = STOCK_ITEM_CURSOR_BASE64URL_ALPHABET.indexOf(character);
    remainingBits = (remainingBits << 6) | value;
    remainingBitCount += 6;
    if (remainingBitCount >= 8) {
      remainingBitCount -= 8;
      bytes.push((remainingBits >>> remainingBitCount) & 0xff);
      remainingBits &= (1 << remainingBitCount) - 1;
    }
  }
  return remainingBits === 0 ? bytes : null;
}

function readStockCursorUtf16(bytes: readonly number[], start: number, end: number): string {
  const codeUnits: number[] = [];
  for (let index = start; index < end; index += 2) {
    codeUnits.push((bytes[index] ?? 0) * 0x100 + (bytes[index + 1] ?? 0));
  }
  return String.fromCharCode(...codeUnits);
}

function decodeCompactStockItemCursor(cursor: string): StockItemCursor | null {
  const encoded = cursor.slice(STOCK_ITEM_CURSOR_COMPACT_PREFIX.length);
  const bytes = decodeStockCursorBytes(encoded);
  if (bytes === null || bytes.length < 6) return null;
  const normalizedNameLength = (bytes[0] ?? 0) * 0x100 + (bytes[1] ?? 0);
  const normalizedNameEnd = 2 + normalizedNameLength * 2;
  if (normalizedNameEnd + 2 > bytes.length || (bytes.length - normalizedNameEnd) % 2 !== 0) {
    return null;
  }
  const result = stockItemCursorPayloadSchema.safeParse({
    normalizedName: readStockCursorUtf16(bytes, 2, normalizedNameEnd),
    id: readStockCursorUtf16(bytes, normalizedNameEnd, bytes.length),
  });
  if (!result.success || encodeStockItemCursor(result.data) !== cursor) return null;
  return result.data;
}

function decodeLegacyStockItemCursor(cursor: string): StockItemCursor | null {
  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(cursor));
    const result = stockItemCursorPayloadSchema.safeParse(parsed);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

export function encodeStockItemCursor(cursor: StockItemCursor): string {
  const payload = stockItemCursorPayloadSchema.parse(cursor);
  const bytes = [payload.normalizedName.length >>> 8, payload.normalizedName.length & 0xff];
  appendStockCursorUtf16Bytes(bytes, payload.normalizedName);
  appendStockCursorUtf16Bytes(bytes, payload.id);
  return STOCK_ITEM_CURSOR_COMPACT_PREFIX + encodeStockCursorBytes(bytes);
}

export function decodeStockItemCursor(cursor: string): StockItemCursor | null {
  return cursor.startsWith(STOCK_ITEM_CURSOR_COMPACT_PREFIX)
    ? decodeCompactStockItemCursor(cursor)
    : decodeLegacyStockItemCursor(cursor);
}

export const stockItemCursorSchema = z
  .string()
  .trim()
  .min(1)
  .refine((value) => decodeStockItemCursor(value) !== null, 'Curseur Stock invalide.');

export const stockItemListQuerySchema = z
  .object({
    cursor: stockItemCursorSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
    search: z.string().trim().max(200).optional(),
    scope: stockScopeSchema.optional(),
    locationId: entityIdSchema.optional(),
    unassigned: z
      .enum(['true', 'false'])
      .transform((value) => value === 'true')
      .optional(),
    state: z.enum(['active', 'archived', 'all']).default('active'),
    selectedId: entityIdSchema.optional(),
  })
  .strict();

export const stockItemCountersSchema = z
  .object({
    filtered: z.number().int().nonnegative(),
    active: z.number().int().nonnegative(),
    archived: z.number().int().nonnegative(),
    lowStock: z.number().int().nonnegative(),
    unassigned: z.number().int().nonnegative(),
  })
  .strict();

export const stockItemPageSchema = z
  .object({
    items: z.array(stockItemSchema),
    page: pageMetaSchema,
    counters: stockItemCountersSchema,
  })
  .strict();

const stockMovementCommonInput = {
  itemId: entityIdSchema,
  revision: z.number().int().positive(),
  occurredAt: isoDateTimeSchema,
  notes: stockMovementNotesInputSchema.default(''),
} as const;

const stockEntryInputSchema = z
  .object({
    ...stockMovementCommonInput,
    kind: z.literal('entry'),
    quantity: positiveStockQuantitySchema,
  })
  .strict();

const stockPurchaseInputSchema = z
  .object({
    ...stockMovementCommonInput,
    kind: z.literal('purchase'),
    quantity: positiveStockQuantitySchema,
  })
  .strict();

const stockPermanentExitInputSchema = z
  .object({
    ...stockMovementCommonInput,
    kind: z.literal('exit'),
    temporary: z.literal(false),
    quantity: positiveStockQuantitySchema,
  })
  .strict();

const stockTemporaryExitInputSchema = z
  .object({
    ...stockMovementCommonInput,
    kind: z.literal('exit'),
    temporary: z.literal(true),
    quantity: positiveStockQuantitySchema,
    startsOn: isoDateSchema,
    expectedReturnOn: isoDateSchema,
  })
  .strict()
  .refine((value) => value.startsOn <= value.expectedReturnOn, {
    message: 'La date de retour doit suivre la date de début.',
    path: ['expectedReturnOn'],
  });

const stockDiscardInputSchema = z
  .object({
    ...stockMovementCommonInput,
    kind: z.literal('discard'),
    quantity: positiveStockQuantitySchema,
  })
  .strict();

const stockMaintenanceInputSchema = z
  .object({
    ...stockMovementCommonInput,
    kind: z.literal('maintenance'),
    quantity: positiveStockQuantitySchema,
    startsOn: isoDateSchema,
    expectedReturnOn: isoDateSchema,
  })
  .strict()
  .refine((value) => value.startsOn <= value.expectedReturnOn, {
    message: 'La date de retour doit suivre la date de début.',
    path: ['expectedReturnOn'],
  });

const stockControlInputSchema = z
  .object({
    ...stockMovementCommonInput,
    kind: z.literal('control'),
    quantity: stockQuantitySchema,
  })
  .strict();

export const stockMovementInputSchema = z.union([
  stockEntryInputSchema,
  stockPurchaseInputSchema,
  stockPermanentExitInputSchema,
  stockTemporaryExitInputSchema,
  stockDiscardInputSchema,
  stockMaintenanceInputSchema,
  stockControlInputSchema,
]);

export const stockMovementKindSchema = z.enum([
  'entry',
  'purchase',
  'exit',
  'discard',
  'maintenance',
  'control',
  'return',
]);

export const stockMovementSchema = z
  .object({
    id: entityIdSchema,
    itemId: entityIdSchema,
    kind: stockMovementKindSchema,
    quantity: stockQuantitySchema,
    totalDelta: signedStockQuantitySchema,
    availableDelta: signedStockQuantitySchema,
    temporary: z.boolean(),
    startsOn: isoDateSchema.nullable(),
    expectedReturnOn: isoDateSchema.nullable(),
    occurredAt: stockCanonicalIsoInstantSchema,
    notes: stockMovementNotesSchema,
    sourceMovementId: entityIdSchema.nullable(),
    returnMovementId: entityIdSchema.nullable(),
    closedAt: stockCanonicalIsoInstantSchema.nullable(),
    returnable: z.boolean(),
    legacy: z.boolean(),
  })
  .strict();

const stockMovementCursorPayloadSchema = z
  .object({ occurredAt: stockCanonicalIsoInstantSchema, id: entityIdSchema })
  .strict();

export type StockMovementCursor = z.infer<typeof stockMovementCursorPayloadSchema>;

export function encodeStockMovementCursor(cursor: StockMovementCursor): string {
  return encodeURIComponent(JSON.stringify(stockMovementCursorPayloadSchema.parse(cursor)));
}

export function decodeStockMovementCursor(cursor: string): StockMovementCursor | null {
  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(cursor));
    const result = stockMovementCursorPayloadSchema.safeParse(parsed);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

export const stockMovementCursorSchema = z
  .string()
  .trim()
  .min(1)
  .refine((value) => decodeStockMovementCursor(value) !== null, 'Curseur mouvement invalide.');

export const stockMovementListQuerySchema = z
  .object({
    itemId: entityIdSchema,
    cursor: stockMovementCursorSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
  })
  .strict();

export const stockMovementPageSchema = z
  .object({ items: z.array(stockMovementSchema), page: pageMetaSchema })
  .strict();

export const stockReturnInputSchema = z
  .object({
    revision: z.number().int().positive(),
    occurredAt: isoDateTimeSchema,
    notes: stockMovementNotesInputSchema.default(''),
  })
  .strict();

export const stockErrorCodeSchema = z.enum([
  'STOCK_ITEM_NOT_FOUND',
  'STOCK_LOCATION_NOT_FOUND',
  'STOCK_REVISION_CONFLICT',
  'STOCK_REFERENCE_CONFLICT',
  'STOCK_INSUFFICIENT',
  'STOCK_MOVEMENT_INVALID',
  'STOCK_MOVEMENT_NOT_TEMPORARY',
  'STOCK_MOVEMENT_ALREADY_RETURNED',
  'STOCK_OPEN_TEMPORARY_MOVEMENT',
  'STOCK_FORBIDDEN',
]);

export type StockScope = z.infer<typeof stockScopeSchema>;
export type StockLocation = z.infer<typeof stockLocationSchema>;
export type StockLocationInput = z.infer<typeof stockLocationInputSchema>;
export type StockItem = z.infer<typeof stockItemSchema>;
export type StockItemCreateInput = z.infer<typeof stockItemCreateInputSchema>;
export type StockItemUpdateInput = z.infer<typeof stockItemUpdateInputSchema>;
export type StockItemListQuery = z.infer<typeof stockItemListQuerySchema>;
export type StockMovement = z.infer<typeof stockMovementSchema>;
export type StockMovementInput = z.infer<typeof stockMovementInputSchema>;
export type StockMovementListQuery = z.infer<typeof stockMovementListQuerySchema>;
export type StockReturnInput = z.infer<typeof stockReturnInputSchema>;
